/**
 * The name of an icon-only control (#6559 round 7f, Claude Design).
 *
 * An `aria-label` names a glyph for assistive tech and leaves every sighted
 * user guessing, which is exactly what happened with the row's hover strip.
 * The tooltip is that name, made visible.
 *
 * Configured once here so call sites cannot drift: above the control, no
 * arrow, 500ms in and nothing out. 500ms is the number for a hover strip —
 * short enough to answer a deliberate pause, long enough that dragging the
 * pointer down twenty rows fires nothing. Keyboard focus opens it instantly,
 * because a focus ring has already signalled the intent a hover has to prove.
 *
 * Touch gets nothing: the hover strip does not exist on a phone, and the
 * sheet that replaces it carries visible text labels.
 *
 * The label is the verb and nothing else — "Details", "Download", "More" —
 * and it must be the control's `aria-label` verbatim, or the two names for
 * one control drift apart.
 *
 * A trigger that owns a menu passes `surfaceOpen`, and the tooltip drops for
 * as long as that surface is open, re-arming only once the pointer leaves and
 * re-enters — otherwise it flashes back the instant the menu closes under a
 * stationary cursor. That is the same rule `MenuTriggerTooltip` carries for
 * triggers with a visible label (#11426 nooks N8, CD ruling); this is the
 * icon-only entry point to it.
 */
import React from "react";
import { Tooltip } from "@mui/material";

export interface HintProps {
  /** The verb. Matches the child's `aria-label` exactly. */
  label: string;
  /** Set when this control owns a menu or popover that is currently open. */
  surfaceOpen?: boolean;
  children: React.ReactElement;
}

export const Hint: React.FC<HintProps> = ({
  label,
  surfaceOpen = false,
  children,
}) => {
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const childProps = children.props as React.HTMLAttributes<HTMLElement>;
  React.useEffect(() => {
    if (surfaceOpen) {
      setHovered(false);
      setFocused(false);
    }
  }, [surfaceOpen]);
  return (
    <Tooltip
      title={label}
      placement="top"
      // Controlled, because MUI applies one delay to hover and focus alike
      // and focus has to be instant.
      open={(hovered || focused) && !surfaceOpen}
      onOpen={() => setHovered(true)}
      onClose={() => setHovered(false)}
      enterDelay={500}
      leaveDelay={0}
      disableInteractive
      disableTouchListener
      slotProps={{
        popper: {
          modifiers: [{ name: "offset", options: { offset: [0, 4] } }],
        },
        tooltip: {
          sx: {
            backgroundColor: "var(--fp-ink)",
            color: "var(--fp-surface)",
            fontSize: 12,
            fontWeight: 500,
            padding: "4px 8px",
            borderRadius: "5px",
          },
        },
      }}
    >
      {React.cloneElement(children, {
        // Using the control dismisses its name: whatever the click opened is
        // what the reader is looking at now.
        onClick: (event: React.MouseEvent<HTMLElement>) => {
          setHovered(false);
          setFocused(false);
          childProps.onClick?.(event);
        },
        // Composed, not replaced: this is a shared export and a child may
        // have its own focus handling (round 7f review N5).
        onFocus: (event: React.FocusEvent<HTMLElement>) => {
          // Keyboard focus only. A mouse click focuses the button too, so
          // counting every focus left the tooltip standing open over the
          // thing the click just opened, with no pointer move to dismiss it.
          setFocused(event.target.matches(":focus-visible"));
          childProps.onFocus?.(event);
        },
        onBlur: (event: React.FocusEvent<HTMLElement>) => {
          setFocused(false);
          childProps.onBlur?.(event);
        },
      } as Partial<React.HTMLAttributes<HTMLElement>>)}
    </Tooltip>
  );
};
